// useAdminAuth.ts — Admin-scoped authentication hook.
//
// Extends the useAuth pattern from the main game (src/hooks/useAuth.ts) with
// an admin allowlist check. After II login succeeds, calls
// dominus_admin.is_admin(principal) to verify the caller has access.
//
// Security model (canister-security skill pattern):
//   - II login gives us a principal + identity.
//   - We call is_admin(principal) as a QUERY (fast, no cycles cost).
//   - The canister enforces the gate on every UPDATE call anyway — the
//     frontend check is UX hardening, not a security boundary.
//   - Non-admins see a denial screen. No dashboard surface is rendered.
//
// @dfinity/auth-client v1.4.0 pin held per CLAUDE.md memory entry.
// DO NOT migrate to @icp-sdk/auth without a dedicated session.

import type { Identity } from '@dfinity/agent';
import { AuthClient } from '@dfinity/auth-client';
import { Ed25519KeyIdentity } from '@dfinity/identity';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createAdminAgent, createAdminActor } from '../lib/adminAgent';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// 8-hour delegation — mirrors the main game's session length.
const MAX_TIME_TO_LIVE = BigInt(8 * 60 * 60 * 1_000_000_000);

// 60-minute idle timeout — admin console is less time-sensitive than gameplay.
const IDLE_TIMEOUT_MS = 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// DEV BYPASS — mock identity injection (extends the existing allowlist bypass)
// ---------------------------------------------------------------------------
//
// When VITE_ADMIN_DEV_BYPASS === 'true' AND VITE_DFX_NETWORK !== 'ic', the II
// popup is skipped entirely — a deterministic Ed25519 mock identity is injected
// instead. The seed is persisted per browser-profile in localStorage so reloads
// keep the same principal. Mirrors src/hooks/useAuth.ts:55-141 pattern.
//
// Production safety: double-guarded (env var AND non-'ic' network). Either
// guard alone would be sufficient; both together make this branch statically
// unreachable in any production build.
//
// Independent from the main game's seed (different localStorage key) — the
// admin surface deserves its own mock principal.

const ADMIN_E2E_SEED_KEY = 'dominus_admin_e2e_seed';

function isAdminDevBypassActive(): boolean {
  return (
    import.meta.env.VITE_ADMIN_DEV_BYPASS === 'true' &&
    import.meta.env.VITE_DFX_NETWORK !== 'ic'
  );
}

function getOrCreateAdminE2ESeed(): Uint8Array {
  try {
    const stored = window.localStorage.getItem(ADMIN_E2E_SEED_KEY);
    if (stored) {
      try {
        const arr = JSON.parse(stored);
        if (Array.isArray(arr) && arr.length === 32 && arr.every((n) => typeof n === 'number')) {
          return new Uint8Array(arr);
        }
      } catch {
        // Corrupt entry — fall through and regenerate
      }
    }
    const seed = new Uint8Array(32);
    crypto.getRandomValues(seed);
    window.localStorage.setItem(ADMIN_E2E_SEED_KEY, JSON.stringify(Array.from(seed)));
    return seed;
  } catch {
    // localStorage unavailable — produce a non-persistent random seed so at
    // least the session works (each reload yields a new principal).
    const seed = new Uint8Array(32);
    crypto.getRandomValues(seed);
    return seed;
  }
}

function buildAdminMockIdentity(): Ed25519KeyIdentity {
  return Ed25519KeyIdentity.generate(getOrCreateAdminE2ESeed());
}

function getIdentityProviderUrl(): string {
  const envUrl = import.meta.env.VITE_II_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim() !== '') {
    return envUrl.trim();
  }
  const isProduction = import.meta.env.VITE_DFX_NETWORK === 'ic';
  // canonical identity.ic0.app in prod per CLAUDE.md anti-hallucination note.
  if (isProduction) return 'https://identity.ic0.app';
  // Local II URL is dynamic — written to VITE_II_URL by deploy-local.sh on every fresh-test.
  // Port 4947 matches dfx.json local binding.
  return import.meta.env.VITE_II_URL || 'http://localhost:4947';
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export type AdminAuthState =
  | 'loading'           // Initial session restore / login in progress
  | 'unauthenticated'   // No II session
  | 'checking_admin'    // II session established; verifying allowlist
  | 'denied'            // II session OK but principal not in admin allowlist
  | 'authenticated';    // II session + admin verified

export interface UseAdminAuthReturn {
  state: AdminAuthState;
  principal: string | null;
  identity: Identity | null;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useAdminAuth(): UseAdminAuthReturn {
  const [state, setState] = useState<AdminAuthState>('loading');
  const [principal, setPrincipal] = useState<string | null>(null);
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [error, setError] = useState<string | null>(null);

  const authClientRef = useRef<AuthClient | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // -------------------------------------------------------------------------
  // Idle timeout
  // -------------------------------------------------------------------------

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  // -------------------------------------------------------------------------
  // Admin allowlist check
  // -------------------------------------------------------------------------

  const checkAdminAllowlist = useCallback(async (currentIdentity: Identity): Promise<boolean> => {
    // DEV-ONLY admin gate bypass. Production (VITE_DFX_NETWORK='ic') always
    // enforces the canister allowlist. The double-guard (env var AND non-'ic'
    // network) makes this path unreachable in production no matter what.
    // See docs/launch/admin-setup.md §5.4 for the production wiring.
    if (
      import.meta.env.VITE_ADMIN_DEV_BYPASS === 'true' &&
      import.meta.env.VITE_DFX_NETWORK !== 'ic'
    ) {
      console.warn(
        '[useAdminAuth] DEV BYPASS active — admin allowlist gate disabled. ' +
        'Any II principal will be treated as admin. This path is unreachable in production.'
      );
      return true;
    }

    try {
      const agent = await createAdminAgent(currentIdentity);
      const adminActor = createAdminActor(agent);
      const principalObj = currentIdentity.getPrincipal();
      // is_admin is a query — fast and free (canister-security skill: query for reads).
      const isAdmin = await adminActor.is_admin(principalObj);
      return isAdmin;
    } catch (err) {
      // Canister not reachable (local dev without admin canister deployed).
      // In dev mode, allow through so the UI can be developed.
      const isDev = import.meta.env.VITE_DFX_NETWORK !== 'ic';
      if (isDev) {
        console.warn(
          '[useAdminAuth] Admin canister unreachable. ' +
          'Allowing through in dev mode. Set VITE_ADMIN_CANISTER_ID to enable real checks.',
          err
        );
        return true;
      }
      throw err;
    }
  }, []);

  // -------------------------------------------------------------------------
  // Sync identity state + admin check
  // -------------------------------------------------------------------------

  const syncIdentityState = useCallback(async (authClient: AuthClient) => {
    const authenticated = await authClient.isAuthenticated();

    if (!authenticated) {
      setState('unauthenticated');
      setPrincipal(null);
      setIdentity(null);
      return;
    }

    const currentIdentity = authClient.getIdentity();
    const principalText = currentIdentity.getPrincipal().toText();
    setPrincipal(principalText);
    setIdentity(currentIdentity);

    // Admin allowlist check
    setState('checking_admin');
    try {
      const isAdmin = await checkAdminAllowlist(currentIdentity);
      if (isAdmin) {
        setState('authenticated');
      } else {
        setState('denied');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Admin verification failed.';
      setError(message);
      setState('denied');
    }
  }, [checkAdminAllowlist]);

  // -------------------------------------------------------------------------
  // Logout
  // -------------------------------------------------------------------------

  const logout = useCallback(async () => {
    setState('loading');
    setError(null);

    // DEV BYPASS — clear the persisted seed so the next bypass-login generates
    // a fresh principal. Skip AuthClient.logout() (no client was created).
    if (isAdminDevBypassActive()) {
      try {
        window.localStorage.removeItem(ADMIN_E2E_SEED_KEY);
      } catch {
        // localStorage unavailable — non-fatal
      }
      clearIdleTimer();
      setPrincipal(null);
      setIdentity(null);
      setState('unauthenticated');
      return;
    }

    try {
      const authClient = authClientRef.current;
      if (authClient) {
        await authClient.logout();
      }
    } catch (err) {
      console.error('[useAdminAuth] Logout error:', err);
    } finally {
      clearIdleTimer();
      setPrincipal(null);
      setIdentity(null);
      setState('unauthenticated');
    }
  }, [clearIdleTimer]);

  // -------------------------------------------------------------------------
  // Login
  // -------------------------------------------------------------------------

  const login = useCallback(async () => {
    setState('loading');
    setError(null);

    // DEV BYPASS — skip the II popup entirely, inject a deterministic mock
    // identity. The allowlist gate at checkAdminAllowlist() also short-circuits
    // (already in place); together this lands the operator on the dashboard
    // immediately. Production unreachable: double-guarded.
    if (isAdminDevBypassActive()) {
      console.warn(
        '[useAdminAuth] DEV BYPASS active — skipping II popup, injecting mock identity. ' +
        'This path is unreachable in production.'
      );
      const mockIdentity = buildAdminMockIdentity();
      const principalText = mockIdentity.getPrincipal().toText();
      setIdentity(mockIdentity);
      setPrincipal(principalText);
      // Allowlist already returns true under bypass — go straight to authenticated.
      setState('authenticated');
      return;
    }

    try {
      let authClient = authClientRef.current;
      if (!authClient) {
        authClient = await AuthClient.create({
          idleOptions: {
            disableDefaultIdleCallback: true,
            idleTimeout: IDLE_TIMEOUT_MS,
          },
        });
        authClientRef.current = authClient;
      }

      await new Promise<void>((resolve, reject) => {
        authClient.login({
          identityProvider: getIdentityProviderUrl(),
          maxTimeToLive: MAX_TIME_TO_LIVE,
          onSuccess: () => resolve(),
          onError: (errMsg) => reject(new Error(errMsg ?? 'Internet Identity login failed')),
        });
      });

      await syncIdentityState(authClient);

      // Set idle timer — logout after prolonged inactivity.
      clearIdleTimer();
      idleTimerRef.current = setTimeout(() => {
        logout().catch(console.error);
      }, IDLE_TIMEOUT_MS);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed. Please try again.';
      console.error('[useAdminAuth] Login error:', err);
      setError(message);
      setState('unauthenticated');
    }
  }, [syncIdentityState, clearIdleTimer, logout]);

  // -------------------------------------------------------------------------
  // Session restoration on mount
  // -------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      // DEV BYPASS — hydrate the mock identity immediately. Skip AuthClient
      // construction entirely; the operator does not need to click anything.
      if (isAdminDevBypassActive()) {
        if (cancelled) return;
        const mockIdentity = buildAdminMockIdentity();
        const principalText = mockIdentity.getPrincipal().toText();
        setIdentity(mockIdentity);
        setPrincipal(principalText);
        setState('authenticated');
        return;
      }

      try {
        const authClient = await AuthClient.create({
          idleOptions: {
            disableDefaultIdleCallback: true,
            idleTimeout: IDLE_TIMEOUT_MS,
          },
        });

        if (cancelled) return;
        authClientRef.current = authClient;

        const authenticated = await authClient.isAuthenticated();
        if (authenticated && !cancelled) {
          await syncIdentityState(authClient);
          // Arm idle timer for restored session.
          if (!cancelled) {
            clearIdleTimer();
            idleTimerRef.current = setTimeout(() => {
              logout().catch(console.error);
            }, IDLE_TIMEOUT_MS);
          }
        } else if (!cancelled) {
          setState('unauthenticated');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[useAdminAuth] Session restore error:', err);
          setState('unauthenticated');
        }
      }
    }

    restoreSession();

    return () => {
      cancelled = true;
      clearIdleTimer();
    };
  }, [syncIdentityState, clearIdleTimer, logout]);

  return { state, principal, identity, error, login, logout };
}
